import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled, { StyledProps } from 'styled-components'
import ReactSelect, { components } from 'react-select'
import { defaultTheme } from '../../../theme'
import { dynamicInputCss, focusInputCss, InputStyled } from '../Input/styles'
import { CountryCodeEntry, matchCallingCode } from '../../../utils'
import { FormField } from '../../form'
import { helpersObject } from '../../../helpers'
import {
  CountryCode,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  isValidNumberForRegion,
} from 'libphonenumber-js'
import { Progress } from '../../../globalTypes'
import { Flag, Spinner } from '../../misc'

const SelectInput: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({ autoComplete: _, ...props }) => (
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore wrong react-select library typings
  <components.Input {...props} autoComplete="no" />
)

const SingleValue: React.FC<React.InputHTMLAttributes<HTMLDivElement>> = props => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore wrong react-select library typings
  return <components.SingleValue {...props}>{props.data.singleValueLabel}</components.SingleValue>
}

type StyledDropdownProps = StyledProps<{ menuIsOpen: boolean }>
const DropdownIndicatorStyled = styled.div<StyledDropdownProps>`
  position: absolute;
  right: -8px;
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  margin-right: 15px;

  ${(props: StyledDropdownProps) => ({
    [props.menuIsOpen ? 'borderBottom' : 'borderTop']: '5px solid #858585;',
  })}
`

const customStyles = {
  control: base => ({
    ...base,
    height: '38px',
    minWidth: '85px',
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    borderRadius: '4px',
    borderColor: defaultTheme.color.onSurfaceLowEmphasys,
    borderRight: 'none',
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    border: 'none',
    outline: 'none',
    boxShadow: 'none',
  }),
  option: (base, props) => ({
    ...base,
    height: '38px',
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    borderRadius: '4px',
    borderColor: defaultTheme.color.onSurfaceLowEmphasys,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    color: props.isSelected ? '#fff' : '#000',
  }),
  menuList: base => ({
    ...base,
    padding: '4px',
  }),
  menu: base => ({
    ...base,
    minWidth: 'max-content',
  }),
  valueContainer: base => ({
    ...base,
    padding: '0 4px 0 8px',
    display: 'flex',
    justifyContent: 'flex-start',
  }),
}

const InputsContainer = styled.div<StyledProps<{ disabled?: boolean; invalid?: boolean; focused: boolean }>>`
  display: grid;
  grid-template-columns: 85px auto;
  border-width: 1px;
  border-style: solid;
  border-radius: 4px;
  height: 40px;
  ${dynamicInputCss}
  ${props => props.focused && focusInputCss(props)}
`

const PhoneInputStyled = styled(InputStyled)`
  border: none;
  height: 38px;
  padding-left: 10px;
`

const OptionStyled = styled.span`
  display: flex;
  align-items: center;
  font-style: normal;
  font-weight: 500;
`

const FlagStyled = styled.span`
  font-size: 18px;
  padding-right: 8px;
  display: flex;
  align-items: center;
`

const CodeStyled = styled.span`
  font-weight: 600;
  margin-left: 4px;
`

const SpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  &::after {
    content: '';
    position: absolute;
    background-color: hsl(0, 0%, 80%);
    width: 1px;
    height: calc(100% - 16px);
    top: 8px;
    right: 0;
  }
`

const makeFullNumber = (code, number) => `+${code || ''}${number || ''}`

type OptionValue = { value: CountryCode; label: string; code: string }

export type CountryPhoneInputProps = {
  defaultCountryCode?: CountryCode
  countryOptions: CountryCodeEntry[]
  countryOptionsProgress?: Progress
  phoneNumber?: string
  onChange(
    fullNumber: string,
    callingCode: string,
    nationalNumber: string,
    isValid: boolean,
    countryCode?: CountryCode,
  ): void
  invalid?: boolean
  errorText?: string
  label?: string
  name: string
  id?: string
  onFocus?(e: React.FocusEvent<HTMLElement>): void
  onBlur?(e: React.FocusEvent<HTMLElement>): void
  className?: string
  disabled?: boolean
  required?: boolean
  testId?: string
}
export const CountryPhoneInput: React.FC<CountryPhoneInputProps> = ({
  defaultCountryCode,
  phoneNumber,
  onChange,
  invalid,
  label,
  id,
  name,
  onFocus,
  onBlur,
  errorText,
  className,
  disabled,
  required,
  testId,
  countryOptions = [],
  countryOptionsProgress,
}) => {
  const parsedNumber = phoneNumber ? parsePhoneNumberFromString(phoneNumber) || null : null
  const prevDefaultCountryCode = useRef(defaultCountryCode)
  const [focused, setFocused] = useState(false)
  const [countryCode, setCountryCode] = useState(parsedNumber?.country || defaultCountryCode)
  const [callingCode, setCallingCode] = useState(
    parsedNumber?.countryCallingCode || (defaultCountryCode && matchCallingCode(defaultCountryCode)),
  )
  const [nationalNumber, setNationalNumber] = useState<string>((parsedNumber?.nationalNumber as string) || '')

  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (prevDefaultCountryCode.current !== defaultCountryCode) {
      setCountryCode(parsedNumber?.country || defaultCountryCode)
      setCallingCode(parsedNumber?.countryCallingCode || (defaultCountryCode && matchCallingCode(defaultCountryCode)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCountryCode])

  const options = useMemo(() => {
    return countryOptions.filter(helpersObject.nonNullable).map(entry => ({
      value: entry.id,
      singleValueLabel: (
        <OptionStyled>
          <FlagStyled>
            <Flag code={entry.id} />
          </FlagStyled>
          +{entry.phone_prefix}
        </OptionStyled>
      ),
      label: (
        <OptionStyled>
          <FlagStyled>
            <Flag code={entry.id} />
          </FlagStyled>
          <span>{entry.name}</span>
          <CodeStyled>{` +${entry.phone_prefix}`}</CodeStyled>
        </OptionStyled>
      ),
      code: entry.phone_prefix,
    }))
  }, [countryOptions])

  const selectedOption = useMemo(() => options.find(opt => opt.value === countryCode), [countryCode, options])

  const handleChange = useCallback(
    (number: string, callingCode?: string, countryCode?: CountryCode) => {
      const fullNumber = makeFullNumber(callingCode || '', number || '')
      const code = `+${callingCode || ''}`
      const isValid = isValidNumberForRegion(number, countryCode as CountryCode)
      onChange(fullNumber, code, number || '', isValid, countryCode)
    },
    [onChange],
  )

  const handleNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/\D/g, '')
      const fullNumber = makeFullNumber(callingCode, value)
      const parsedNumber = parsePhoneNumberFromString(fullNumber)
      setNationalNumber((parsedNumber?.nationalNumber as string) || value || '')
      handleChange((parsedNumber?.nationalNumber as string) || value || '', callingCode as string, countryCode)
    },
    [countryCode, callingCode, handleChange],
  )

  const handleCountryCodeChange = useCallback(
    (code: CountryCode) => {
      const callingCode = getCountryCallingCode(code) as string
      setCountryCode(code)
      setCallingCode(callingCode)
      handleChange(nationalNumber, callingCode, code)
    },
    [handleChange, nationalNumber],
  )

  const handleFocus: React.FocusEventHandler = useCallback(
    (e: React.FocusEvent<HTMLElement>) => {
      setFocused(true)
      onFocus && onFocus(e)
    },
    [onFocus],
  )

  const handleBlur: React.FocusEventHandler = useCallback(
    (e: React.FocusEvent<HTMLElement>) => {
      setFocused(false)
      onBlur && onBlur(e)
    },
    [onBlur],
  )

  const handleSelectClose = useCallback(() => {
    inputRef.current?.focus()
  }, [])

  return (
    <FormField
      id={id}
      name={name}
      invalid={invalid}
      errorText={errorText}
      className={className}
      label={label}
      required={required}
      testId={testId}
      render={({ id, name }) => (
        <InputsContainer focused={focused} invalid={invalid}>
          {countryOptionsProgress === Progress.WORK ? (
            <SpinnerWrapper>
              <Spinner size={'small'} />
            </SpinnerWrapper>
          ) : (
            <ReactSelect
              id={name}
              instanceId={name}
              name={`${name}-select`}
              isSearchable={false}
              onFocus={handleFocus}
              placeholder={''}
              noOptionsMessage={() => 'Empty'}
              styles={customStyles}
              options={options}
              value={selectedOption}
              onChange={v => handleCountryCodeChange(((v as unknown) as OptionValue).value)}
              onMenuClose={handleSelectClose}
              isDisabled={disabled}
              components={{
                Input: SelectInput,
                SingleValue,
                // eslint-disable-next-line react/display-name
                DropdownIndicator: ({ selectProps }) => (
                  <DropdownIndicatorStyled menuIsOpen={!!selectProps.menuIsOpen} />
                ),
              }}
            />
          )}

          <PhoneInputStyled
            id={id}
            name={name}
            onBlur={handleBlur}
            onFocus={handleFocus}
            value={nationalNumber}
            onChange={handleNumberChange}
            disabled={disabled || countryOptionsProgress === Progress.WORK}
            data-field-invalid={invalid}
            autoComplete={'tel-national'}
            ref={inputRef}
            inputMode="numeric"
            pattern={'[0-9]*'}
            type={'tel'}
          />
        </InputsContainer>
      )}
    />
  )
}
